import { useEffect, useReducer, useRef } from "react";
import { AlertCircle, Check, Copy } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import {
  usePreviewOtcBaseContractLanguage,
  usePreviewOtcLanguage,
} from "@/lib/api/otcLanguagePreview";
import type { PossibleReturnType } from "@/lib/responseResults";
import type { Customer, Trader } from "@/lib/api/reports";
import type {
  OtcBaseContractLanguagePreviewRequest,
  OtcContractCreatePayload,
  OtcLanguagePreviewAmendment,
  OtcLanguagePreviewData,
  OtcLanguagePreviewRequest,
  OtcLanguagePreviewType,
} from "@/lib/validation/otcLanguagePreview";
import { OtcAmendmentWizard } from "@/section/otc/contract-form-v3";

export type OtcLanguagePreviewLayerOption = {
  value: string;
  label: string;
  layerNo: number;
  snapshot: Record<string, unknown>;
  quantity: string | undefined;
};

type HypotheticalAmendmentOptions = {
  traders: Trader[];
  customers: Customer[];
};

type OtcLanguagePreviewModalProps = {
  open: boolean;
  layerOptions: OtcLanguagePreviewLayerOption[];
  initialLayerId: string | null;
  amendment?: OtcLanguagePreviewAmendment;
  sampleContract?: OtcContractCreatePayload;
  hypotheticalAmendmentOptions?: HypotheticalAmendmentOptions;
  onClose: () => void;
};

type PreviewIntent =
  | "existing_contract"
  | "existing_amendment"
  | "existing_invoice"
  | "new_amendment"
  | "new_invoice";

type PreviewState = {
  selectedLayerId: string;
  intent: PreviewIntent;
  language: string | null;
  error: string | null;
  status: "idle" | "generating";
  copied: boolean;
};

type PreviewAction =
  | { type: "reset"; state: PreviewState }
  | { type: "select_layer"; layerId: string; intent: PreviewIntent }
  | { type: "select_intent"; intent: PreviewIntent }
  | { type: "generation_started" }
  | { type: "generation_succeeded"; language: string }
  | { type: "generation_failed"; message: string }
  | { type: "copied" };

const INTENT_LABELS: Record<PreviewIntent, string> = {
  existing_contract: "Use Existing Contract",
  existing_amendment: "Use Existing Amendment",
  existing_invoice: "Use Existing Invoice",
  new_amendment: "New Amendment",
  new_invoice: "New Invoice",
};

function defaultIntentForLayer(
  layer: OtcLanguagePreviewLayerOption | undefined,
): PreviewIntent {
  return layer?.layerNo === 1 ? "existing_contract" : "existing_amendment";
}

function createPreviewState({
  layerOptions,
  initialLayerId,
  sampleContract,
  amendment,
}: Pick<
  OtcLanguagePreviewModalProps,
  "layerOptions" | "initialLayerId" | "sampleContract" | "amendment"
>): PreviewState {
  const selectedLayerId = initialLayerId ?? layerOptions[0]?.value ?? "";
  const selectedLayer = layerOptions.find(
    (option: OtcLanguagePreviewLayerOption): boolean =>
      option.value === selectedLayerId,
  );

  return {
    selectedLayerId,
    intent:
      sampleContract !== undefined
        ? "existing_contract"
        : amendment !== undefined
          ? "new_amendment"
          : defaultIntentForLayer(selectedLayer),
    language: null,
    error: null,
    status: "idle",
    copied: false,
  };
}

function previewReducer(
  state: PreviewState,
  action: PreviewAction,
): PreviewState {
  switch (action.type) {
    case "reset":
      return action.state;
    case "select_layer":
      return {
        ...state,
        selectedLayerId: action.layerId,
        intent: action.intent,
        language: null,
        error: null,
        copied: false,
      };
    case "select_intent":
      return {
        ...state,
        intent: action.intent,
        language: null,
        error: null,
        copied: false,
      };
    case "generation_started":
      return {
        ...state,
        status: "generating",
        language: null,
        error: null,
        copied: false,
      };
    case "generation_succeeded":
      return {
        ...state,
        status: "idle",
        language: action.language,
        error: null,
      };
    case "generation_failed":
      return {
        ...state,
        status: "idle",
        language: null,
        error: action.message,
      };
    case "copied":
      return { ...state, copied: true };
  }
}

function getIntentOptions({
  selectedLayer,
  amendment,
}: {
  selectedLayer: OtcLanguagePreviewLayerOption | undefined;
  amendment: OtcLanguagePreviewAmendment | undefined;
}): Array<{ value: PreviewIntent; label: string }> {
  if (amendment !== undefined) {
    const intents: PreviewIntent[] = ["new_amendment", "new_invoice"];
    return intents.map((intent: PreviewIntent) => ({
      value: intent,
      label: INTENT_LABELS[intent],
    }));
  }

  const intents: PreviewIntent[] =
    selectedLayer?.layerNo === 1
      ? [
          "existing_contract",
          "existing_invoice",
          "new_amendment",
          "new_invoice",
        ]
      : [
          "existing_amendment",
          "existing_invoice",
          "new_amendment",
          "new_invoice",
        ];

  return intents.map((intent: PreviewIntent) => ({
    value: intent,
    label: INTENT_LABELS[intent],
  }));
}

export function OtcLanguagePreviewModal({
  open,
  layerOptions,
  initialLayerId,
  amendment,
  sampleContract,
  hypotheticalAmendmentOptions,
  onClose,
}: OtcLanguagePreviewModalProps) {
  const { toast } = useToast();
  const previewLanguage = usePreviewOtcLanguage();
  const previewBaseContractLanguage = usePreviewOtcBaseContractLanguage();
  const autoGeneratedSampleRef = useRef<OtcContractCreatePayload | null>(null);
  const [state, dispatch] = useReducer(
    previewReducer,
    createPreviewState({
      layerOptions,
      initialLayerId,
      sampleContract,
      amendment,
    }),
  );
  const { selectedLayerId, intent, language, error, status, copied } = state;
  const isGenerating = status === "generating";
  const firstLayerId = layerOptions[0]?.value ?? "";
  const selectedLayer = layerOptions.find(
    (option: OtcLanguagePreviewLayerOption): boolean =>
      option.value === selectedLayerId,
  );
  const intentOptions = getIntentOptions({ selectedLayer, amendment });
  const requiresAmendmentDraft =
    amendment === undefined &&
    (intent === "new_amendment" || intent === "new_invoice");
  const canEnterAmendmentDraft =
    requiresAmendmentDraft &&
    hypotheticalAmendmentOptions !== undefined &&
    selectedLayer !== undefined;

  useEffect(() => {
    if (!open) return;

    dispatch({
      type: "reset",
      state: createPreviewState({
        layerOptions,
        initialLayerId: initialLayerId ?? firstLayerId,
        sampleContract,
        amendment,
      }),
    });
  }, [open, initialLayerId, firstLayerId, sampleContract, amendment]);

  const generateLanguage = async (
    request: () => Promise<PossibleReturnType<OtcLanguagePreviewData>>,
  ): Promise<void> => {
    if (isGenerating) return;

    dispatch({ type: "generation_started" });
    try {
      const result = await request();

      if (!result.success) {
        dispatch({ type: "generation_failed", message: result.message });
        return;
      }

      dispatch({
        type: "generation_succeeded",
        language: result.data.language,
      });
    } catch (requestError: unknown) {
      dispatch({
        type: "generation_failed",
        message:
          requestError instanceof Error
            ? requestError.message
            : "Failed to generate OTC language preview",
      });
    }
  };

  const generateExistingLanguage = async (): Promise<void> => {
    if (sampleContract !== undefined) {
      const request: OtcBaseContractLanguagePreviewRequest = {
        sample_contract: sampleContract,
      };
      await generateLanguage(() => previewBaseContractLanguage(request));
      return;
    }

    if (selectedLayerId === "") return;

    if (intent === "existing_contract") {
      const request: OtcBaseContractLanguagePreviewRequest = {
        layer_id: selectedLayerId,
      };
      await generateLanguage(() => previewBaseContractLanguage(request));
      return;
    }

    if (intent === "existing_amendment" || intent === "existing_invoice") {
      const type: OtcLanguagePreviewType =
        intent === "existing_amendment" ? "amendment" : "invoice";
      const request: OtcLanguagePreviewRequest = {
        type,
        layer_id: selectedLayerId,
      };
      await generateLanguage(() => previewLanguage(request));
      return;
    }

    if (amendment !== undefined) {
      await generateAmendmentLanguage(amendment);
    }
  };

  useEffect(() => {
    if (!open || sampleContract === undefined) {
      if (!open) autoGeneratedSampleRef.current = null;
      return;
    }
    if (autoGeneratedSampleRef.current === sampleContract) return;

    autoGeneratedSampleRef.current = sampleContract;
    void generateExistingLanguage();
  }, [open, sampleContract]);

  const generateAmendmentLanguage = async (
    nextAmendment: OtcLanguagePreviewAmendment,
  ): Promise<void> => {
    if (selectedLayer === undefined) return;

    const type: OtcLanguagePreviewType =
      intent === "new_invoice" ? "invoice" : "amendment";
    const request: OtcLanguagePreviewRequest = {
      type,
      layer_id: selectedLayer.value,
      amendment: nextAmendment,
    };
    await generateLanguage(() => previewLanguage(request));
  };

  const handleCopy = async (): Promise<void> => {
    if (language === null) return;

    try {
      await navigator.clipboard.writeText(language);
      dispatch({ type: "copied" });
    } catch (copyError: unknown) {
      console.error("Failed to copy OTC language preview", copyError);
      toast({
        title: "Failed to copy language",
        description: "Clipboard access is unavailable.",
      });
    }
  };

  const handleClose = (): void => {
    if (isGenerating) return;
    onClose();
  };

  const resetToExistingIntent = (): void => {
    dispatch({
      type: "select_intent",
      intent: defaultIntentForLayer(selectedLayer),
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen: boolean) => {
        if (!nextOpen) handleClose();
      }}
    >
      <DialogContent
        className={
          canEnterAmendmentDraft
            ? "max-w-none w-[90vw] max-h-[90vh] overflow-y-auto"
            : "max-w-2xl"
        }
      >
        <DialogHeader>
          <DialogTitle>
            {sampleContract === undefined
              ? "OTC Language Preview"
              : "Contract Import Language"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {error !== null && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Unable to generate language</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {sampleContract === undefined && (
            <div
              className={`grid gap-3 ${
                amendment === undefined ? "grid-cols-2" : "grid-cols-1"
              }`}
            >
              {amendment === undefined && (
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Source Layer</Label>
                  <Combobox
                    isModal
                    disabled={isGenerating}
                    value={selectedLayerId}
                    onValueChange={(value: string) => {
                      const layer = layerOptions.find(
                        (option: OtcLanguagePreviewLayerOption): boolean =>
                          option.value === value,
                      );
                      if (layer === undefined) return;

                      dispatch({
                        type: "select_layer",
                        layerId: value,
                        intent: defaultIntentForLayer(layer),
                      });
                    }}
                    options={layerOptions}
                    virtualizeList
                    dwimOnChange
                    className="sm:min-w-[200px] md:min-w-[300px]"
                    popoverClassName="sm:min-w-[200px] md:min-w-[300px]"
                  />
                </div>
              )}

              <div className="grid items-center gap-1.5">
                <Label className="font-semibold">Language</Label>
                <Combobox
                  isModal
                  disabled={isGenerating}
                  value={intent}
                  onValueChange={(value: string) => {
                    const nextIntent = intentOptions.find(
                      (option: { value: PreviewIntent; label: string }): boolean =>
                        option.value === value,
                    )?.value;
                    if (nextIntent === undefined) return;

                    dispatch({
                      type: "select_intent",
                      intent: nextIntent,
                    });
                  }}
                  options={intentOptions}
                  dwimOnChange
                  className="sm:min-w-[200px] md:min-w-[300px]"
                  popoverClassName="sm:min-w-[200px] md:min-w-[300px]"
                />
              </div>
            </div>
          )}

          {language !== null && language === "" && (
            <Alert>
              <AlertDescription>
                No language was generated for this selection.
              </AlertDescription>
            </Alert>
          )}

          {language !== null && language !== "" && (
            <Textarea
              readOnly
              value={language}
              className="min-h-[240px] resize-y whitespace-pre-wrap"
            />
          )}

          {canEnterAmendmentDraft && (
            <OtcAmendmentWizard
              key={selectedLayer.value}
              mode="preview"
              presentation="inline"
              open
              expected_current_layer_id={selectedLayer.value}
              traders={hypotheticalAmendmentOptions.traders}
              customers={hypotheticalAmendmentOptions.customers}
              currentQuantity={selectedLayer.quantity}
              currentSnapshot={selectedLayer.snapshot}
              onClose={resetToExistingIntent}
              onPreviewAmendment={generateAmendmentLanguage}
            />
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isGenerating}
          >
            Close
          </Button>
          {language !== null && language !== "" && (
            <Button type="button" variant="outline" onClick={handleCopy}>
              {copied ? (
                <Check className="mr-2 h-4 w-4" />
              ) : (
                <Copy className="mr-2 h-4 w-4" />
              )}
              {copied ? "Copied" : "Copy to Clipboard"}
            </Button>
          )}
          {!requiresAmendmentDraft && (
            <Button
              type="button"
              onClick={generateExistingLanguage}
              disabled={
                isGenerating ||
                (sampleContract === undefined && selectedLayerId === "")
              }
            >
              {isGenerating
                ? "Generating..."
                : sampleContract !== undefined &&
                    (language !== null || error !== null)
                  ? "Regenerate"
                  : "Generate"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
